import React, { useEffect, useState, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SlideOver } from '../../shared/SlideOver';
import { Button } from '../../../components/ui/Button';
import { Card } from '../../../components/ui/Card';
import { Input } from '../../../components/ui/Input';
import { StudentData } from '../pages/StudentDetailPanel';
import { GroupId } from '../../../config/systemConfig';
import { error as logError } from '../../../utils/logger';
import { genId } from '../../../utils/genId';
import { dataLayer } from '../../../data/dataLayer';
import { Users, Calendar, CheckCircle2, Search, ChevronDown, ChevronRight, Check, CheckSquare, UserCheck, Layers, Target, MessageSquare, X } from 'lucide-react';
import { FAMILIES_BY_MODULE, type FamilyDefinition, MODULES, type ModuleId, getModuleIcon } from '../../../content';
import { DjayNarrator } from '../../../mascot';
interface CreatePracticePanelProps {
  open: boolean;
  onClose: () => void;
  students: StudentData[];
  selectedGroup: GroupId;
  teacherId: string;
  classroomId: string | null;
  onSuccess: () => void;
}
type Step = 1 | 2 | 3;
const STEP_CONFIG = [{
  num: 1,
  label: 'Vakgebieden'
}, {
  num: 2,
  label: 'Onderdelen'
}, {
  num: 3,
  label: 'Klaarzetten'
}] as const;
const stepVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 60 : -60,
    opacity: 0
  }),
  center: {
    x: 0,
    opacity: 1
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -60 : 60,
    opacity: 0
  })
};
export function CreatePracticePanel({
  open,
  onClose,
  students,
  selectedGroup,
  teacherId,
  classroomId,
  onSuccess
}: CreatePracticePanelProps) {
  const [step, setStep] = useState<Step>(1);
  const [direction, setDirection] = useState(1);
  const [selectedModuleIds, setSelectedModuleIds] = useState<ModuleId[]>([]);
  const [selectedFamilyIds, setSelectedFamilyIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSkills, setExpandedSkills] = useState<Record<string, boolean>>({});
  const [selectionMode, setSelectionMode] = useState<'whole' | 'specific' | null>(null);
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  const [deadline, setDeadline] = useState<string>('');
  const [note, setNote] = useState<string>('');
  const [title, setTitle] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [hideTip, setHideTip] = useState(false);
  useEffect(() => {
    const pref = localStorage.getItem('mp.teacher.hidePracticeTip');
    if (pref === 'true') setHideTip(true);
  }, []);
  const dismissTip = () => {
    setHideTip(true);
    localStorage.setItem('mp.teacher.hidePracticeTip', 'true');
  };
  useEffect(() => {
    if (open) {
      setStep(1);
      setDirection(1);
      setSelectedModuleIds([]);
      setSelectionMode(null);
      setSelectedStudentIds([]);
      setSelectedFamilyIds([]);
      setDeadline('');
      setNote('');
      setTitle('');
      setSearchQuery('');
      setExpandedSkills({});
      setIsSubmitting(false);
      setShowSuccess(false);
    }
  }, [open]);
  useEffect(() => {
    // When modules change, clear families that are no longer in selected modules
    setSelectedFamilyIds(prev => {
      const validFamilies = selectedModuleIds.flatMap(modId => FAMILIES_BY_MODULE.get(modId) || []).map(f => f.familyId);
      return prev.filter(id => validFamilies.includes(id));
    });
  }, [selectedModuleIds]);
  const handleNext = () => {
    if (step < 3) {
      setDirection(1);
      setStep(s => s + 1 as Step);
    }
  };
  const handleBack = () => {
    if (step > 1) {
      setDirection(-1);
      setStep(s => s - 1 as Step);
    }
  };
  const handleConfirm = async () => {
    if (selectedModuleIds.length === 0) return;
    setIsSubmitting(true);
    const assignmentId = genId('practice');
    const wholeClass = selectionMode === 'whole';
    const finalTitle = title.trim() || 'Huiswerk Oefenen';
    const assignment = {
      id: assignmentId,
      teacherId,
      classroomId,
      title: finalTitle,
      moduleIds: selectedModuleIds,
      targetFamilyIds: selectedFamilyIds,
      targetStudentIds: wholeClass ? [] : selectedStudentIds,
      wholeClass,
      questionCount: 20,
      createdAt: Date.now(),
      deadline: deadline ? new Date(deadline).getTime() : null,
      status: 'active' as const,
      completedByStudentIds: [],
      note: note.trim() || undefined
    };
    try {
      await dataLayer.savePracticeAssignment(assignmentId, assignment);
      setShowSuccess(true);
    } catch (error) {
      logError('Failed to save practice assignment', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  const toggleModule = (id: ModuleId) => {
    setSelectedModuleIds(prev => prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id]);
  };
  const toggleStudent = (id: string) => {
    setSelectedStudentIds(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  };
  const isNextDisabled = () => {
    if (step === 1 && selectedModuleIds.length === 0) return true;
    // Step 2 is optional (can just assign whole modules)
    if (step === 3 && selectionMode === null) return true;
    if (step === 3 && selectionMode === 'specific' && selectedStudentIds.length === 0) return true;
    return false;
  };
  const renderStepIndicator = () => <div className="flex items-center gap-1 mb-4" data-id="element-8487">
      {STEP_CONFIG.map((s, i) => {
      const isActive = step === s.num;
      const isDone = step > s.num;
      return <Fragment key={s.num} data-id="element-8488">
            {i !== 0 && <div className={`flex-1 h-[2px] rounded-full transition-colors duration-300 ${isDone ? 'bg-indigo-500' : 'bg-slate-200'}`} data-id="element-8489" />}
            <div className="flex flex-col items-center gap-1" data-id="element-8490">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${isDone ? 'bg-indigo-500 text-white' : isActive ? 'bg-indigo-500 text-white ring-4 ring-indigo-100' : 'bg-slate-100 text-slate-400'}`} data-id="element-8491">
                {isDone ? <Check className="w-3.5 h-3.5" data-id="element-8492" /> : s.num}
              </div>
              <span className={`text-[10px] font-bold uppercase tracking-wider whitespace-nowrap ${isActive ? 'text-indigo-600' : isDone ? 'text-indigo-500' : 'text-slate-400'}`} data-id="element-8493">
                {s.label}
              </span>
            </div>
          </Fragment>;
    })}
    </div>;
  const renderSummaryStrip = () => {
    const hasModules = selectedModuleIds.length > 0;
    const hasFamilies = selectedFamilyIds.length > 0;
    const hasStudents = selectionMode !== null;
    if (!hasModules && step === 1) return null;
    return <motion.div initial={{
      height: 0,
      opacity: 0
    }} animate={{
      height: 'auto',
      opacity: 1
    }} className="border-t border-slate-100 pt-3 pb-1" data-id="element-8494">
        <div className="flex items-center gap-3 flex-wrap" data-id="element-8495">
          {hasModules && <div className="flex items-center gap-1.5 bg-slate-50 rounded-full px-2.5 py-1 border border-slate-200" data-id="element-8496">
              <Target className="w-3 h-3 text-slate-500" data-id="element-8497" />
              <span className="text-xs font-semibold text-slate-700" data-id="element-8498">
                {selectedModuleIds.length} vakken
              </span>
            </div>}
          {hasFamilies ? <div className="flex items-center gap-1.5 bg-slate-50 rounded-full px-2.5 py-1 border border-slate-200" data-id="element-8499">
              <Layers className="w-3 h-3 text-slate-500" data-id="element-8500" />
              <span className="text-xs font-semibold text-slate-700" data-id="element-8501">
                {selectedFamilyIds.length} onderdelen
              </span>
            </div> : hasModules && <div className="flex items-center gap-1.5 bg-amber-50 rounded-full px-2.5 py-1 border border-amber-200" title="Geen specifieke onderdelen gekozen — leerlingen krijgen vragen uit alle onderdelen van de gekozen vakken" data-id="element-8502">
                <Layers className="w-3 h-3 text-amber-600" data-id="element-8503" />
                <span className="text-xs font-semibold text-amber-800" data-id="element-8504">
                  Alle onderdelen
                </span>
              </div>}
          {hasStudents && <div className="flex items-center gap-1.5 bg-slate-50 rounded-full px-2.5 py-1 border border-slate-200" data-id="element-8505">
              <UserCheck className="w-3 h-3 text-slate-500" data-id="element-8506" />
              <span className="text-xs font-semibold text-slate-700" data-id="element-8507">
                {selectionMode === 'whole' ? `${students.length} leerlingen` : `${selectedStudentIds.length} leerling(en)`}
              </span>
            </div>}
          {deadline && <div className="flex items-center gap-1.5 bg-slate-50 rounded-full px-2.5 py-1 border border-slate-200" data-id="element-8508">
              <Calendar className="w-3 h-3 text-slate-500" data-id="element-8509" />
              <span className="text-xs font-semibold text-slate-700" data-id="element-8510">
                {new Date(deadline).toLocaleDateString('nl-NL', {
              day: 'numeric',
              month: 'short'
            })}
              </span>
            </div>}
        </div>
      </motion.div>;
  };
  const renderStep1 = () => <div className="flex flex-col h-full" data-id="element-8511">
      <div className="flex items-center gap-2.5 mb-4" data-id="element-8512">
        <p className="text-sm font-medium text-slate-600" data-id="element-8513">
          Kies één of meerdere vakgebieden om te oefenen voor{' '}
          <span className="font-bold text-slate-900" data-id="element-8514">
            groep {selectedGroup}
          </span>
        </p>
      </div>

      {!hideTip && <div className="mb-4 bg-indigo-50 border border-indigo-100 rounded-xl p-3 flex gap-3 relative" data-id="element-8515">
          <button onClick={dismissTip} className="absolute top-2 right-2 text-indigo-400 hover:text-indigo-600" data-id="element-8516">
            <X className="w-4 h-4" data-id="element-8517" />
          </button>
          <div className="mt-1" data-id="element-8518">
            <DjayNarrator size="xs" emotion="happy" data-id="element-8519" />
          </div>
          <div className="text-sm text-indigo-800 pr-6" data-id="element-8520">
            <span className="font-bold" data-id="element-8521">Tip van Djay:</span> Modules zijn de
            onderwerpen binnen een vak — bijvoorbeeld 'Breuken' of
            'Werkwoordspelling'.
          </div>
        </div>}

      <div className="grid grid-cols-2 gap-2.5 flex-1 content-start" data-id="element-8522">
        {MODULES.map(module => {
        const isSelected = selectedModuleIds.includes(module.id);
        const IconComp = getModuleIcon(module.icon);
        return <div key={module.id} onClick={() => toggleModule(module.id)} className={`p-3 rounded-xl border-2 transition-all cursor-pointer flex flex-col items-center text-center ${isSelected ? 'border-indigo-500 bg-indigo-50/60 shadow-sm' : 'border-slate-200 hover:border-indigo-200 bg-white'}`} data-id="element-8523">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-2 ${isSelected ? 'bg-indigo-100 text-indigo-600' : 'bg-slate-100 text-slate-500'}`} data-id="element-8524">
                <IconComp className="w-5 h-5" data-id="element-8525" />
              </div>
              <h3 className={`font-bold text-sm ${isSelected ? 'text-indigo-900' : 'text-slate-900'}`} data-id="element-8526">
                {module.title}
              </h3>
              {isSelected && <div className="absolute top-2 right-2" data-id="element-8527">
                  <CheckCircle2 className="w-4 h-4 text-indigo-500" data-id="element-8528" />
                </div>}
            </div>;
      })}
      </div>
    </div>;
  const renderStep2 = () => {
    const allFamilies = selectedModuleIds.flatMap(modId => FAMILIES_BY_MODULE.get(modId) || []);
    const filteredFamilies = allFamilies.filter(f => f.description.toLowerCase().includes(searchQuery.toLowerCase()) || f.skill.toLowerCase().includes(searchQuery.toLowerCase()));
    const grouped: Record<string, typeof allFamilies> = {};
    filteredFamilies.forEach(f => {
      if (!grouped[f.skill]) grouped[f.skill] = [];
      grouped[f.skill].push(f);
    });
    const toggleFamily = (id: string) => {
      setSelectedFamilyIds(prev => prev.includes(id) ? prev.filter(fid => fid !== id) : [...prev, id]);
    };
    const toggleSkillGroup = (familiesInSkill: FamilyDefinition[]) => {
      const ids = familiesInSkill.map(f => f.familyId);
      const allSelected = ids.every(id => selectedFamilyIds.includes(id));
      if (allSelected) {
        setSelectedFamilyIds(prev => prev.filter(id => !ids.includes(id)));
      } else {
        setSelectedFamilyIds(prev => Array.from(new Set([...prev, ...ids])));
      }
    };
    const toggleExpand = (skill: string) => {
      setExpandedSkills(prev => ({
        ...prev,
        [skill]: !prev[skill]
      }));
    };
    const handleSelectAll = () => {
      if (selectedFamilyIds.length === allFamilies.length) {
        setSelectedFamilyIds([]);
      } else {
        setSelectedFamilyIds(allFamilies.map(f => f.familyId));
      }
    };
    const selectedCount = selectedFamilyIds.length;
    const isAllSelected = allFamilies.length > 0 && selectedCount === allFamilies.length;
    return <div className="flex flex-col h-full" data-id="element-8529">
        <div className="flex items-center gap-2.5 mb-3" data-id="element-8530">
          <p className="text-sm font-medium text-slate-600" data-id="element-8531">
            Kies welke onderdelen je leerlingen oefenen.
          </p>
        </div>

        {selectedCount === 0 && <div className="mb-3 flex items-start gap-2.5 p-3 rounded-xl bg-amber-50 border border-amber-200" data-id="element-8532">
            <div className="mt-0.5 text-amber-600" data-id="element-8533">
              <Target className="w-4 h-4" data-id="element-8534" />
            </div>
            <div className="text-xs text-amber-900 leading-relaxed" data-id="element-8535">
              <span className="font-bold" data-id="element-8536">Nog niets gekozen.</span> Zonder
              selectie krijgen de leerlingen vragen uit{' '}
              <span className="font-bold" data-id="element-8537">
                alle {allFamilies.length} onderdelen
              </span>{' '}
              van de gekozen{' '}
              {selectedModuleIds.length === 1 ? 'module' : 'modules'} — dus ook
              onderdelen die je misschien niet bedoelde.
            </div>
          </div>}

        <div className="flex items-center justify-between mb-2" data-id="element-8538">
          <div className="flex items-center gap-2" data-id="element-8539">
            <span className="text-sm font-bold text-indigo-600" data-id="element-8540">
              {selectedCount}
            </span>
            <span className="text-xs text-slate-500" data-id="element-8541">
              / {allFamilies.length} geselecteerd
            </span>
          </div>
          <button onClick={handleSelectAll} className="flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-700 px-2 py-1 rounded-lg hover:bg-indigo-50 transition-colors" data-id="element-8542">
            <CheckSquare className="w-3.5 h-3.5" data-id="element-8543" />
            {isAllSelected ? 'Deselecteer' : 'Alles'}
          </button>
        </div>

        <div className="relative mb-3" data-id="element-8544">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-8545" />
          <Input placeholder="Zoek..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 bg-white h-10 text-sm" data-id="element-8546" />
        </div>

        <div className="flex-1 overflow-y-auto -mx-1 px-1 space-y-1.5 pb-2" data-id="element-8547">
          {Object.entries(grouped).map(([skill, families]) => {
          const isExpanded = expandedSkills[skill];
          const familyIds = families.map(f => f.familyId);
          const selectedInSkill = familyIds.filter(id => selectedFamilyIds.includes(id)).length;
          const isAllSkillSelected = selectedInSkill === families.length;
          const isPartiallySelected = selectedInSkill > 0 && selectedInSkill < families.length;
          return <div key={skill} className="border border-slate-200 rounded-lg overflow-hidden bg-white" data-id="element-8548">
                <div className="flex items-center justify-between min-h-[44px] px-3" data-id="element-8549">
                  <div className="flex items-center gap-2 cursor-pointer flex-1 py-2" onClick={() => toggleExpand(skill)} data-id="element-8550">
                    {isExpanded ? <ChevronDown className="w-4 h-4 text-slate-400" data-id="element-8551" /> : <ChevronRight className="w-4 h-4 text-slate-400" data-id="element-8552" />}
                    <span className="text-sm font-semibold text-slate-700 truncate" data-id="element-8553">
                      {skill.replace(/_/g, ' ')}
                    </span>
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0 ${isAllSkillSelected ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-500'}`} data-id="element-8554">
                      {selectedInSkill}/{families.length}
                    </span>
                  </div>
                  <div className="cursor-pointer p-2 -mr-1" onClick={() => toggleSkillGroup(families)} data-id="element-8555">
                    <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${isAllSkillSelected ? 'bg-indigo-500 border-indigo-500 text-white' : isPartiallySelected ? 'bg-indigo-100 border-indigo-400 text-indigo-500' : 'border-slate-300 bg-white'}`} data-id="element-8556">
                      {isAllSkillSelected && <Check className="w-3 h-3" data-id="element-8557" />}
                      {isPartiallySelected && <div className="w-2 h-0.5 bg-current rounded-full" data-id="element-8558" />}
                    </div>
                  </div>
                </div>

                {isExpanded && <div className="border-t border-slate-100" data-id="element-8559">
                    {families.map(f => {
                const isSelected = selectedFamilyIds.includes(f.familyId);
                return <div key={f.familyId} onClick={() => toggleFamily(f.familyId)} className="flex items-center gap-3 px-3 py-2.5 min-h-[44px] cursor-pointer transition-colors hover:bg-slate-50 border-b border-slate-50 last:border-b-0" data-id="element-8560">
                          <div className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${isSelected ? 'bg-indigo-500 border-indigo-500 text-white' : 'border-slate-300 bg-white'}`} data-id="element-8561">
                            {isSelected && <Check className="w-3 h-3" data-id="element-8562" />}
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-8563">
                            <span className={`text-sm truncate block ${isSelected ? 'font-semibold text-slate-900' : 'text-slate-600'}`} data-id="element-8564">
                              {f.description}
                            </span>
                          </div>
                          <span className="text-[10px] text-slate-400 font-bold uppercase flex-shrink-0" data-id="element-8565">
                            {f.levelCode}
                          </span>
                        </div>;
              })}
                  </div>}
              </div>;
        })}
          {filteredFamilies.length === 0 && <div className="text-center py-8 text-slate-400 text-sm" data-id="element-8566">
              <div className="mb-3 flex justify-center" data-id="element-8567">
                <DjayNarrator size="md" emotion="thinking" data-id="element-8568" />
              </div>
              Geen resultaten voor "{searchQuery}"
            </div>}
        </div>
      </div>;
  };
  const renderStep3 = () => {
    return <div className="flex flex-col gap-4 pb-4" data-id="element-8569">
        <div className="flex items-center gap-2.5" data-id="element-8570">
          <p className="text-sm font-medium text-slate-600" data-id="element-8571">
            Bijna klaar! Kies de leerlingen en voeg details toe.
          </p>
        </div>

        <div data-id="element-8572">
          <label className="text-xs font-semibold text-slate-500 mb-1 block" data-id="element-8573">
            Titel (optioneel)
          </label>
          <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Bijv. Breuken oefenen" className="h-9 text-sm" data-id="element-8574" />
        </div>

        <div className="grid grid-cols-2 gap-2" data-id="element-8575">
          <div onClick={() => setSelectionMode('whole')} className={`p-3 rounded-xl border-2 cursor-pointer transition-all text-center ${selectionMode === 'whole' ? 'border-indigo-500 bg-indigo-50/60' : 'border-slate-200 hover:border-indigo-200 bg-white'}`} data-id="element-8576">
            <Users className={`w-5 h-5 mx-auto mb-1 ${selectionMode === 'whole' ? 'text-indigo-500' : 'text-slate-400'}`} data-id="element-8577" />
            <div className={`text-sm font-semibold ${selectionMode === 'whole' ? 'text-indigo-900' : 'text-slate-700'}`} data-id="element-8578">
              Hele klas
            </div>
            <div className="text-[11px] text-slate-400" data-id="element-8579">
              {students.length} leerlingen
            </div>
          </div>
          <div onClick={() => setSelectionMode('specific')} className={`p-3 rounded-xl border-2 cursor-pointer transition-all text-center ${selectionMode === 'specific' ? 'border-indigo-500 bg-indigo-50/60' : 'border-slate-200 hover:border-indigo-200 bg-white'}`} data-id="element-8580">
            <UserCheck className={`w-5 h-5 mx-auto mb-1 ${selectionMode === 'specific' ? 'text-indigo-500' : 'text-slate-400'}`} data-id="element-8581" />
            <div className={`text-sm font-semibold ${selectionMode === 'specific' ? 'text-indigo-900' : 'text-slate-700'}`} data-id="element-8582">
              Selectie
            </div>
            <div className="text-[11px] text-slate-400" data-id="element-8583">Kies leerlingen</div>
          </div>
        </div>

        {selectionMode === 'specific' && <div className="border border-slate-200 rounded-xl overflow-hidden" data-id="element-8584">
            <div className="px-3 py-2 bg-slate-50 text-xs font-bold text-slate-600 border-b border-slate-200" data-id="element-8585">
              {selectedStudentIds.length} van {students.length} geselecteerd
            </div>
            <div className="max-h-[150px] overflow-y-auto divide-y divide-slate-100" data-id="element-8586">
              {students.map(student => {
            const isSelected = selectedStudentIds.includes(student.id);
            return <div key={student.id} onClick={() => toggleStudent(student.id)} className={`flex items-center justify-between px-3 py-2 min-h-[44px] cursor-pointer transition-colors ${isSelected ? 'bg-indigo-50/40' : 'hover:bg-slate-50'}`} data-id="element-8587">
                    <div className="flex items-center gap-2.5" data-id="element-8588">
                      <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-600" data-id="element-8589">
                        {student.name.charAt(0).toUpperCase()}
                      </div>
                      <span className="text-sm font-medium text-slate-800" data-id="element-8590">
                        {student.name}
                      </span>
                    </div>
                    <div className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${isSelected ? 'bg-indigo-500 border-indigo-500 text-white' : 'border-slate-300 bg-white'}`} data-id="element-8591">
                      {isSelected && <Check className="w-3 h-3" data-id="element-8592" />}
                    </div>
                  </div>;
          })}
            </div>
          </div>}

        <div className="flex items-center gap-3" data-id="element-8593">
          <Calendar className="w-4 h-4 text-slate-400 flex-shrink-0" data-id="element-8594" />
          <div className="flex-1" data-id="element-8595">
            <label className="text-xs font-semibold text-slate-500 mb-0.5 block" data-id="element-8596">
              Deadline (optioneel)
            </label>
            <Input type="date" value={deadline} onChange={e => setDeadline(e.target.value)} className="h-9 text-sm" min={new Date().toISOString().split('T')[0]} data-id="element-8597" />
          </div>
        </div>

        <div className="flex items-start gap-3" data-id="element-8598">
          <MessageSquare className="w-4 h-4 text-slate-400 flex-shrink-0 mt-2" data-id="element-8599" />
          <div className="flex-1" data-id="element-8600">
            <label className="text-xs font-semibold text-slate-500 mb-0.5 block" data-id="element-8601">
              Bericht voor leerlingen (optioneel)
            </label>
            <textarea value={note} onChange={e => setNote(e.target.value)} placeholder="Bijv. Succes met oefenen!" className="w-full text-sm border border-slate-200 rounded-lg p-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none resize-none h-20" data-id="element-8602" />
          </div>
        </div>
      </div>;
  };
  return <SlideOver open={open} onClose={onClose} title="Huiswerk Opgeven" description={<span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-bold bg-indigo-100 text-indigo-700" data-id="element-8604">
          Groep {selectedGroup}
        </span>} width="lg" data-id="element-8603">
      <div className="flex flex-col h-full" data-id="element-8605">
        {showSuccess ? <div className="flex-1 flex flex-col items-center justify-center p-6 text-center" data-id="element-8606">
            <motion.div initial={{
          scale: 0.8,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          type: 'spring',
          bounce: 0.5
        }} className="mb-6" data-id="element-8607">
              <DjayNarrator size="lg" emotion="proud" data-id="element-8608" />
            </motion.div>
            <motion.div initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          delay: 0.1
        }} className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mb-4" data-id="element-8609">
              <CheckCircle2 className="w-8 h-8" data-id="element-8610" />
            </motion.div>
            <motion.h2 initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          delay: 0.2
        }} className="text-2xl font-black text-slate-900 mb-2" data-id="element-8611">
              Huiswerk opgegeven! 🎉
            </motion.h2>
            <motion.p initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          delay: 0.3
        }} className="text-slate-600 mb-6 max-w-sm" data-id="element-8612">
              Het huiswerk voor{' '}
              <span className="font-bold" data-id="element-8613">
                {selectionMode === 'whole' ? 'de hele klas' : `${selectedStudentIds.length} leerling(en)`}
              </span>{' '}
              is klaargezet —{' '}
              <span className="font-bold" data-id="element-8614">
                {selectedFamilyIds.length > 0 ? `${selectedFamilyIds.length} onderdelen` : `alle onderdelen van ${selectedModuleIds.length === 1 ? 'het gekozen vak' : 'de gekozen vakken'}`}
              </span>
              .
              {deadline && <>
                  {' '}
                  Deadline:{' '}
                  <span className="font-bold" data-id="element-8615">
                    {new Date(deadline).toLocaleDateString('nl-NL', {
                day: 'numeric',
                month: 'long'
              })}
                  </span>
                  .
                </>}
            </motion.p>
            <motion.div initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          delay: 0.5
        }} data-id="element-8616">
              <Button onClick={() => {
            onSuccess();
            onClose();
          }} className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 h-11 font-semibold" data-id="element-8617">
                Sluiten
              </Button>
            </motion.div>
          </div> : <>
            {renderStepIndicator()}
            <div className="flex-1 overflow-hidden relative" data-id="element-8618">
              <AnimatePresence mode="wait" custom={direction} data-id="element-8619">
                <motion.div key={step} custom={direction} variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.2,
              ease: 'easeInOut'
            }} className="absolute inset-0 overflow-y-auto px-0.5" data-id="element-8620">
                  {step === 1 && renderStep1()}
                  {step === 2 && renderStep2()}
                  {step === 3 && renderStep3()}
                </motion.div>
              </AnimatePresence>
            </div>
            <div className="flex-shrink-0 bg-white" data-id="element-8621">
              {renderSummaryStrip()}
              <div className="flex items-center justify-between pt-3 pb-safe" data-id="element-8622">
                <Button variant="ghost" onClick={step === 1 ? onClose : handleBack} className="text-slate-500 hover:text-slate-700 hover:bg-slate-100 h-11 px-4" data-id="element-8623">
                  {step === 1 ? 'Annuleren' : 'Terug'}
                </Button>
                {step < 3 ? <Button onClick={handleNext} disabled={isNextDisabled()} className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm h-11 px-6 font-semibold" data-id="element-8624">
                    Volgende <ChevronRight className="w-4 h-4 ml-1" data-id="element-8625" />
                  </Button> : <Button onClick={handleConfirm} disabled={isSubmitting || isNextDisabled()} className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm h-11 px-6 font-semibold" data-id="element-8626">
                    {isSubmitting ? 'Bezig...' : 'Opgeven'}
                  </Button>}
              </div>
            </div>
          </>}
      </div>
    </SlideOver>;
}